
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuContent,
  NavigationMenuTrigger,
  NavigationMenuLink
} from "@/components/ui/navigation-menu";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Menu } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose
} from "@/components/ui/sheet";

export const Navbar = () => {
  // Static list of providers
  const providers = [
    { name: 'CBB', slug: 'cbb' },
    { name: 'Eesy', slug: 'eesy' },
    { name: 'Flexii', slug: 'flexii' },
    { name: 'Greentel', slug: 'greentel' },
    { name: 'Oister', slug: 'oister' },
    { name: 'Telenor', slug: 'telenor' },
  ];

  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
      <div className="container mx-auto px-4">
        <div className="h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-4">
            <Logo />
            <span className="text-sm text-muted-foreground hidden sm:inline-block">
              Nemmere bliver det ikke!
            </span>
          </Link>
          
          {isMobile ? (
            // Mobile menu with optimized rendering
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="ml-auto"
                  aria-label="Menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent 
                side="right" 
                className="w-[85vw] sm:max-w-sm border-l"
              >
                <div className="flex flex-col gap-6 mt-6">
                  <div className="flex flex-col space-y-3 border-b pb-6">
                    <p className="text-sm font-semibold">Udbydere</p>
                    {providers.map((provider) => (
                      <SheetClose key={provider.slug} asChild>
                        <Link 
                          to={`/mobiludbyder/${provider.slug}`}
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          onClick={() => setIsOpen(false)}
                        >
                          {provider.name}
                        </Link>
                      </SheetClose>
                    ))}
                    <SheetClose asChild>
                      <Link 
                        to="/mobiludbydere"
                        className="text-primary font-medium hover:text-primary/80"
                        onClick={() => setIsOpen(false)}
                      >
                        Se alle udbydere
                      </Link>
                    </SheetClose>
                  </div>
                  
                  <div className="flex flex-col space-y-4">
                    <SheetClose asChild>
                      <Link 
                        to="/om-os" 
                        className="text-foreground hover:text-primary transition-colors"
                        onClick={() => setIsOpen(false)}
                      >
                        Om os
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link 
                        to="/kontakt" 
                        className="text-foreground hover:text-primary transition-colors"
                        onClick={() => setIsOpen(false)}
                      >
                        Kontakt
                      </Link>
                    </SheetClose>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          ) : (
            // Desktop menu
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent/50">
                    Udbydere
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="bg-popover p-2 min-w-[200px]">
                    <ul className="grid w-full gap-1 p-2">
                      {providers.map((provider) => (
                        <li key={provider.slug}>
                          <Link 
                            to={`/mobiludbyder/${provider.slug}`}
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          >
                            <div className="text-sm font-medium leading-none">
                              {provider.name}
                            </div>
                          </Link>
                        </li>
                      ))}
                      <li className="mt-2 pt-2 border-t border-border">
                        <Link 
                          to="/mobiludbydere"
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        >
                          <div className="text-sm font-medium leading-none text-primary">
                            Se alle udbydere
                          </div>
                        </Link>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
                
                <NavigationMenuItem>
                  <Link to="/om-os">
                    <Button variant="ghost">Om os</Button>
                  </Link>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <Link to="/kontakt">
                    <Button variant="ghost">Kontakt</Button>
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          )}
        </div>
      </div>
    </nav>
  );
};
